import { Head, router, usePage } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import { type ContextFn, format, type Locale } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import type { TFunction } from "i18next";
import {
	CopyIcon,
	EyeIcon,
	EyeOffIcon,
	ListFilterIcon,
	Loader2,
	MoreHorizontal,
	MousePointerClick,
	PencilIcon,
	PlusIcon,
	PowerIcon,
	SearchIcon,
	Trash2Icon,
	TriangleAlertIcon,
	XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import ServiceCredentialFormFields from "@/components/admin-portal/service-credentials/upsert-form-fields";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { useDateContext } from "@/components/providers/date-provider";
import {
	ResponsiveDialog,
	type ResponsiveDialogProps,
} from "@/components/shared/responsive-dialog";
import { Alert, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { cn, copyToClipboard, populateQueryParams } from "@/lib/utils";
import type {
	ServiceCredential,
	ServiceCredentialFilterOptions,
} from "@/types/admin-portal/service-credential";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export interface PageProps {
	serviceCredentials: {
		data: ServiceCredential[];
		metadata: Metadata;
	};
	filterOptions: ServiceCredentialFilterOptions;
}

export interface ServiceCredentialIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: unknown;
}

const ALL_OPTION = "all";

/** Query params the controller preserves across a mutation redirect. */
const PRESERVED_FILTER_KEYS = [
	"q",
	"service_type",
	"status",
	"last_used_from",
	"last_used_to",
	"last_used_never",
	"page",
	"limit",
];

/** Props reloaded on every filter/pagination visit — keeps the payload small. */
const RELOAD_ONLY = [
	"serviceCredentials",
	"filterOptions",
	"adminPortal",
	"flash",
	"errors",
];

type FormDialogState = {
	mode: "create" | "edit";
	credential: ServiceCredential | null;
};

/**
 * A destructive action waiting on confirmation. Revoke is reversible but still
 * disables a live credential, so it confirms the same way delete does.
 */
type PendingAction = {
	kind: "revoke" | "delete";
	credential: ServiceCredential;
};

export default function Index({
	serviceCredentials,
	filterOptions,
}: PageProps) {
	const { props: globalProps } =
		usePage<ServiceCredentialIndexGlobalPageProps>();
	const { locale, tzDate } = useDateContext();
	const { toast } = useToast();
	const { t } = useTranslation("service-credentials");

	const indexRoute =
		globalProps.adminPortal?.router?.adminPortal?.serviceCredentials?.index;
	const toggleRoute =
		globalProps.adminPortal?.router?.adminPortal?.serviceCredentials?.toggle;
	const currentQuery = globalProps.adminPortal?.currentQuery;

	const rows = useMemo(
		() =>
			Array.isArray(serviceCredentials?.data) ? serviceCredentials.data : [],
		[serviceCredentials?.data],
	);
	const metadata = serviceCredentials?.metadata;

	// `current_query` is camelised alongside the rest of the adminPortal prop.
	const currentSearch = currentQuery?.q || "";
	const currentType = currentQuery?.serviceType || "";
	const currentStatus = currentQuery?.status || "";
	const currentLastUsedFrom = currentQuery?.lastUsedFrom || "";
	const currentLastUsedTo = currentQuery?.lastUsedTo || "";
	const currentNeverUsed = Boolean(currentQuery?.lastUsedNever);

	// Open the panel on load if the page arrived with filters already applied, so
	// the active filters are visible rather than hidden behind the toggle.
	const [showFilters, setShowFilters] = useState(
		Boolean(
			currentSearch ||
				currentType ||
				currentStatus ||
				currentLastUsedFrom ||
				currentLastUsedTo ||
				currentNeverUsed,
		),
	);
	const [searchTerm, setSearchTerm] = useState(currentSearch);
	const debouncedSearch = useDebouncedValue(searchTerm, 350);
	// Which rows have their token revealed. Safe to keep client-side: the token
	// is already in the payload (page is super-admin-gated) — this only toggles
	// whether it is shown in plain text.
	const [revealed, setRevealed] = useState<Record<string, boolean>>({});
	// The create/edit dialog lives in React state, not the URL: a validation
	// failure comes back as a redirect, and a URL-driven dialog would unmount and
	// throw away whatever the admin had just typed.
	const [formDialog, setFormDialog] = useState<FormDialogState | null>(null);
	const [pendingAction, setPendingAction] = useState<PendingAction | null>(
		null,
	);
	const [isActionLoading, setIsActionLoading] = useState(false);

	const visit = useCallback(
		(baseUrl: string, params: Record<string, string | null>) => {
			const { fullUrl, queryParams } = populateQueryParams(baseUrl, params);
			router.get(fullUrl, queryParams, {
				only: RELOAD_ONLY,
				preserveScroll: true,
				preserveState: true,
			});
		},
		[],
	);

	// The base params every filter change carries, so switching one filter never
	// silently drops the others.
	const filterParams = useCallback(
		(
			overrides: Record<string, string | null>,
		): Record<string, string | null> => ({
			q: currentSearch || null,
			service_type: currentType || null,
			status: currentStatus || null,
			last_used_from: currentLastUsedFrom || null,
			last_used_to: currentLastUsedTo || null,
			last_used_never: currentNeverUsed ? "true" : null,
			...overrides,
		}),
		[
			currentSearch,
			currentType,
			currentStatus,
			currentLastUsedFrom,
			currentLastUsedTo,
			currentNeverUsed,
		],
	);

	// Any filter change resets to page 1 — staying on page 7 of a narrower result
	// set would render an empty table.
	const applyFilter = useCallback(
		(overrides: Record<string, string | null>) => {
			if (!indexRoute) return;
			visit(indexRoute, { ...filterParams(overrides), page: null });
		},
		[indexRoute, visit, filterParams],
	);

	useEffect(() => {
		if (debouncedSearch === currentSearch) return;
		applyFilter({ q: debouncedSearch || null });
	}, [debouncedSearch, currentSearch, applyFilter]);

	const hasActiveFilters = Boolean(
		currentSearch ||
			currentType ||
			currentStatus ||
			currentLastUsedFrom ||
			currentLastUsedTo ||
			currentNeverUsed,
	);

	const handleClearFilters = useCallback(() => {
		setSearchTerm("");
		applyFilter({
			q: null,
			service_type: null,
			status: null,
			last_used_from: null,
			last_used_to: null,
			last_used_never: null,
		});
	}, [applyFilter]);

	// Mutations target a bare REST path, so the active filters ride along in the
	// query string: the controller rebuilds its redirect target from
	// `request.query_parameters`.
	const filterQuery = useMemo(() => {
		const preserved = new URLSearchParams();
		const current = filterParams({});
		for (const key of PRESERVED_FILTER_KEYS) {
			const value = current[key];
			if (value) preserved.set(key, value);
		}
		const query = preserved.toString();
		return query ? `?${query}` : "";
	}, [filterParams]);

	const changePage = useCallback(
		(
			type: "prev" | "next" | "first" | "last" | "limit" | "page",
			value?: string,
		) => {
			if (!metadata) return;

			let url: string | undefined;
			switch (type) {
				case "prev":
					url = metadata.prevUrl;
					break;
				case "next":
					url = metadata.nextUrl;
					break;
				case "first":
					url = metadata.firstUrl;
					break;
				case "last":
					url = metadata.lastUrl;
					break;
				case "limit": {
					if (!value) return;
					url = populateQueryParams(metadata.pageUrl, {
						limit: value,
						page: null,
					}).fullUrl;
					break;
				}
				case "page": {
					if (!value) return;
					url = populateQueryParams(metadata.pageUrl, { page: value }).fullUrl;
					break;
				}
			}

			if (!url) return;
			router.get(
				url,
				{},
				{ only: RELOAD_ONLY, preserveScroll: false, preserveState: true },
			);
		},
		[metadata],
	);

	const handleToggleReveal = useCallback((id: string) => {
		setRevealed((prev) => ({ ...prev, [id]: !prev[id] }));
	}, []);

	const handleCopy = useCallback(
		(value: string | null | undefined, descriptionKey: string) => {
			if (!value) return;
			copyToClipboard(value);
			toast({
				title: t("toast.copy_success"),
				description: t(descriptionKey),
			});
		},
		[toast, t],
	);

	const submitToggle = useCallback(
		(credential: ServiceCredential, options?: { onFinish?: () => void }) => {
			if (!toggleRoute) return;
			router.patch(
				`${toggleRoute.replace(":id", credential.id)}${filterQuery}`,
				{},
				{
					only: RELOAD_ONLY,
					preserveScroll: true,
					preserveState: true,
					onStart: () => setIsActionLoading(true),
					onFinish: () => {
						setIsActionLoading(false);
						options?.onFinish?.();
					},
				},
			);
		},
		[toggleRoute, filterQuery],
	);

	// Revoking a live credential confirms first; activating a revoked one is a
	// safe, reversible restore, so it happens on the spot.
	const handleToggleActive = useCallback(
		(credential: ServiceCredential) => {
			if (credential.isActive) {
				setIsActionLoading(false);
				setPendingAction({ kind: "revoke", credential });
				return;
			}
			submitToggle(credential);
		},
		[submitToggle],
	);

	const handleConfirmAction = useCallback(() => {
		if (!pendingAction) return;

		if (pendingAction.kind === "revoke") {
			submitToggle(pendingAction.credential, {
				onFinish: () => setPendingAction(null),
			});
			return;
		}

		if (!indexRoute) return;
		router.delete(
			`${indexRoute}/${pendingAction.credential.id}${filterQuery}`,
			{
				only: RELOAD_ONLY,
				preserveScroll: true,
				preserveState: true,
				onStart: () => setIsActionLoading(true),
				onFinish: () => {
					setIsActionLoading(false);
					setPendingAction(null);
				},
			},
		);
	}, [pendingAction, indexRoute, filterQuery, submitToggle]);

	const columns = useMemo<ColumnDef<ServiceCredential>[]>(
		() =>
			getColumns({
				t,
				locale,
				tzDate,
				revealed,
				onToggleReveal: handleToggleReveal,
				onCopy: handleCopy,
				onToggleActive: handleToggleActive,
				onEdit: (credential) => setFormDialog({ mode: "edit", credential }),
				onDelete: (credential) =>
					setPendingAction({ kind: "delete", credential }),
			}),
		[
			t,
			locale,
			tzDate,
			revealed,
			handleToggleReveal,
			handleCopy,
			handleToggleActive,
		],
	);

	const formDialogProps = useMemo<ResponsiveDialogProps>(() => {
		const isEdit = formDialog?.mode === "edit";
		return {
			title: isEdit ? t("modal.update.title") : t("modal.add.title"),
			description: isEdit
				? t("modal.update.description")
				: t("modal.add.description"),
			isOpen: !!formDialog,
			dialogWidth: "640px",
			onOpenChange: () => setFormDialog(null),
		};
	}, [formDialog, t]);

	return (
		<>
			<Head title={t("head_title")} />

			<PageContainer className="min-h-[100vh] min-w-0 flex-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("page_title")}
						</h1>
						<p className="w-full text-sm text-muted-foreground text-pretty md:w-10/12 xl:w-8/12">
							{t("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<Alert>
					<TriangleAlertIcon className="size-4 shrink-0" />
					<AlertTitle className="leading-normal text-pretty">
						{t("warning")}
					</AlertTitle>
				</Alert>

				{/* Toolbar: filter toggle on the left, primary action on the right —
				    the same show/hide filter pattern the Orders page uses. */}
				<div className="z-10 flex items-center justify-between gap-2">
					<Button
						type="button"
						variant={showFilters ? "default" : "outline"}
						className="shadow-none"
						onClick={() => setShowFilters((prev) => !prev)}
					>
						<ListFilterIcon />
						{showFilters ? t("button.close_filter") : t("button.filter")}
					</Button>

					<Button
						className="w-full md:w-fit"
						onClick={() => setFormDialog({ mode: "create", credential: null })}
					>
						<PlusIcon />
						{t("button.add_data")}
					</Button>
				</div>

				<AnimatePresence initial={false}>
					{showFilters ? (
						<motion.section
							key="service-credential-filters"
							initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
							transition={{ duration: 0.25, ease: "easeOut" }}
							className="p-3 border shadow-inner rounded-xl bg-background"
						>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-3 lg:grid-cols-3 xl:grid-cols-4">
								<div className="grid gap-2 col-span-full md:col-span-2">
									<Label className="text-xs text-muted-foreground">
										{t("filter.search_label")}
									</Label>
									<Input
										value={searchTerm}
										onChange={(event) => setSearchTerm(event.target.value)}
										placeholder={t("filter.search_placeholder")}
										aria-label={t("filter.search_label")}
										className="bg-input"
										StartIcon={{ icon: SearchIcon }}
									/>
								</div>

								<div className="grid gap-2">
									<Label className="text-xs text-muted-foreground">
										{t("table.header.service_type")}
									</Label>
									<Select
										value={currentType || ALL_OPTION}
										onValueChange={(value) =>
											applyFilter({
												service_type: value === ALL_OPTION ? null : value,
											})
										}
									>
										<SelectTrigger className="bg-input">
											<SelectValue placeholder={t("filter.all_types")} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={ALL_OPTION}>
												{t("filter.all_types")}
											</SelectItem>
											{filterOptions?.serviceTypes?.map((type) => (
												<SelectItem key={type} value={type}>
													{t(`service_type.${type}`, { defaultValue: type })}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="grid gap-2">
									<Label className="text-xs text-muted-foreground">
										{t("table.header.status")}
									</Label>
									<Select
										value={currentStatus || ALL_OPTION}
										onValueChange={(value) =>
											applyFilter({
												status: value === ALL_OPTION ? null : value,
											})
										}
									>
										<SelectTrigger className="bg-input">
											<SelectValue placeholder={t("filter.all_statuses")} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={ALL_OPTION}>
												{t("filter.all_statuses")}
											</SelectItem>
											{filterOptions?.statuses?.map((status) => (
												<SelectItem key={status} value={status}>
													{t(`status.${status}`, { defaultValue: status })}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{/* A plain date range excludes NULLs, so "never used" is its
								    own option — and it disables the range, which cannot also
								    apply. */}
								<div className="grid gap-2">
									<Label className="text-xs text-muted-foreground">
										{t("filter.last_used_from")}
									</Label>
									<Input
										type="date"
										className="bg-input"
										value={currentLastUsedFrom}
										disabled={currentNeverUsed}
										onChange={(event) =>
											applyFilter({
												last_used_from: event.target.value || null,
											})
										}
									/>
								</div>
								<div className="grid gap-2">
									<Label className="text-xs text-muted-foreground">
										{t("filter.last_used_to")}
									</Label>
									<Input
										type="date"
										className="bg-input"
										value={currentLastUsedTo}
										disabled={currentNeverUsed}
										onChange={(event) =>
											applyFilter({ last_used_to: event.target.value || null })
										}
									/>
								</div>

								<Label className="flex items-center gap-2 text-sm md:col-span-2 md:self-end md:pb-2">
									<Checkbox
										checked={currentNeverUsed}
										onCheckedChange={(checked) =>
											applyFilter({
												last_used_never: checked ? "true" : null,
												last_used_from: checked
													? null
													: currentLastUsedFrom || null,
												last_used_to: checked
													? null
													: currentLastUsedTo || null,
											})
										}
									/>
									{t("filter.never_used")}
								</Label>
							</div>

							<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 md:flex-row">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="w-full md:w-fit"
									onClick={() => setShowFilters(false)}
								>
									{t("button.close_filter")}
								</Button>
								{hasActiveFilters ? (
									<Button
										type="button"
										variant="ghost-destructive"
										size="sm"
										className="w-full md:w-fit"
										onClick={handleClearFilters}
									>
										<XIcon className="size-4" />
										{t("button.clear_filters")}
									</Button>
								) : null}
							</div>
						</motion.section>
					) : null}
				</AnimatePresence>

				<DataTable
					columns={columns}
					data={rows}
					emptyState={
						hasActiveFilters ? (
							<div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
								<div className="flex items-center gap-2">
									<SearchIcon className="size-5" />
									<span>{t("table.empty.filtered")}</span>
								</div>
								<Button
									type="button"
									variant="ghost-destructive"
									size="sm"
									onClick={handleClearFilters}
								>
									<XIcon className="size-4" />
									{t("button.clear_filters")}
								</Button>
							</div>
						) : (
							<div className="flex items-center justify-center gap-2 text-muted-foreground">
								<MousePointerClick className="size-6" />
								<span>{t("table.empty.none")}</span>
							</div>
						)
					}
					customPagination={() =>
						metadata ? (
							<ApptPagination
								label={t("pagination.credentials")}
								metadata={metadata}
								actions={{
									goToPrevpage: () => changePage("prev"),
									goToNextPage: () => changePage("next"),
									onChangeLimit: (value) => changePage("limit", value),
									goToPage: (value) => changePage("page", value),
									goToFirstPage: () => changePage("first"),
									goToLastPage: () => changePage("last"),
								}}
							/>
						) : null
					}
				/>

				{formDialog && (
					<ResponsiveDialog {...formDialogProps}>
						<ServiceCredentialFormFields
							key={formDialog.credential?.id ?? "new"}
							credential={formDialog.credential}
							handleOpenChange={(open) => {
								if (!open) setFormDialog(null);
							}}
						/>
					</ResponsiveDialog>
				)}

				<AlertDialog
					open={!!pendingAction}
					onOpenChange={(open) => {
						if (!open && isActionLoading) return;
						if (!open) setPendingAction(null);
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{pendingAction?.kind === "revoke"
									? t("modal.revoke.title")
									: t("modal.delete.title")}
							</AlertDialogTitle>
							<AlertDialogDescription className="text-pretty">
								{!pendingAction
									? t("modal.delete.fallback_description")
									: pendingAction.kind === "revoke"
										? t("modal.revoke.description", {
												name: pendingAction.credential.serviceName,
											})
										: t("modal.delete.description", {
												name: pendingAction.credential.serviceName,
											})}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isActionLoading}>
								{t("button.cancel")}
							</AlertDialogCancel>
							<Button
								variant="destructive"
								disabled={isActionLoading}
								onClick={(event) => {
									event.preventDefault();
									handleConfirmAction();
								}}
							>
								{isActionLoading ? (
									<>
										<Loader2 className="mr-2 size-4 animate-spin" />
										{pendingAction?.kind === "revoke"
											? t("button.revoking")
											: t("button.deleting")}
									</>
								) : pendingAction?.kind === "revoke" ? (
									t("button.revoke")
								) : (
									t("button.delete")
								)}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</PageContainer>
		</>
	);
}

type ColumnContext = {
	t: TFunction;
	locale: Locale;
	tzDate: ContextFn<Date>;
	revealed: Record<string, boolean>;
	onToggleReveal: (id: string) => void;
	onCopy: (value: string | null | undefined, descriptionKey: string) => void;
	onToggleActive: (credential: ServiceCredential) => void;
	onEdit: (credential: ServiceCredential) => void;
	onDelete: (credential: ServiceCredential) => void;
};

function getColumns({
	t,
	locale,
	tzDate,
	revealed,
	onToggleReveal,
	onCopy,
	onToggleActive,
	onEdit,
	onDelete,
}: ColumnContext): ColumnDef<ServiceCredential>[] {
	return [
		{
			accessorKey: "serviceName",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.service_name")}
				/>
			),
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<span className="font-mono font-semibold text-sm text-nowrap">
						{row.original.serviceName}
					</span>
					<Button
						variant="ghost"
						size="icon"
						className="size-7 opacity-60 hover:opacity-100"
						aria-label={t("button.copy_name")}
						onClick={() =>
							onCopy(row.original.serviceName, "toast.copy_name_description")
						}
					>
						<CopyIcon className="size-3" />
					</Button>
				</div>
			),
			enableSorting: false,
		},
		{
			accessorKey: "serviceToken",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.service_token")}
				/>
			),
			cell: ({ row }) => {
				const isRevealed = !!revealed[row.original.id];
				return (
					<div className="flex items-center gap-1">
						<span className="min-w-0 truncate font-mono text-xs max-w-[16rem]">
							{isRevealed ? row.original.serviceToken : "•".repeat(24)}
						</span>
						<Button
							variant="ghost"
							size="icon"
							className="size-7 opacity-60 hover:opacity-100"
							aria-label={
								isRevealed ? t("button.hide_token") : t("button.reveal_token")
							}
							onClick={() => onToggleReveal(row.original.id)}
						>
							{isRevealed ? (
								<EyeOffIcon className="size-3" />
							) : (
								<EyeIcon className="size-3" />
							)}
						</Button>
						{/* Copies the full token regardless of mask state — the source is
						    the payload value, not what is on screen. */}
						<Button
							variant="ghost"
							size="icon"
							className="size-7 opacity-60 hover:opacity-100"
							aria-label={t("button.copy_token")}
							onClick={() =>
								onCopy(
									row.original.serviceToken,
									"toast.copy_token_description",
								)
							}
						>
							<CopyIcon className="size-3" />
						</Button>
					</div>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "serviceType",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.service_type")}
				/>
			),
			cell: ({ row }) => (
				<Badge variant="accent" className="font-mono text-nowrap">
					{t(`service_type.${row.original.serviceType}`, {
						defaultValue: row.original.serviceType,
					})}
				</Badge>
			),
			enableSorting: false,
		},
		{
			accessorKey: "clientId",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.client_id")}
				/>
			),
			meta: {
				headerClassName: "hidden lg:table-cell",
				cellClassName: "hidden lg:table-cell",
			},
			cell: ({ row }) =>
				row.original.clientId ? (
					<div className="flex items-center gap-2">
						<span className="font-mono text-xs text-nowrap">
							{row.original.clientId}
						</span>
						<Button
							variant="ghost"
							size="icon"
							className="size-7 opacity-60 hover:opacity-100"
							aria-label={t("button.copy_client_id")}
							onClick={() =>
								onCopy(
									row.original.clientId,
									"toast.copy_client_id_description",
								)
							}
						>
							<CopyIcon className="size-3" />
						</Button>
					</div>
				) : (
					<span className="text-muted-foreground italic text-xs">
						{t("table.not_available")}
					</span>
				),
			enableSorting: false,
		},
		{
			accessorKey: "clientName",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.client_name")}
				/>
			),
			meta: {
				headerClassName: "hidden lg:table-cell",
				cellClassName: "hidden lg:table-cell",
			},
			cell: ({ row }) =>
				row.original.clientName || (
					<span className="text-muted-foreground italic text-xs">
						{t("table.not_available")}
					</span>
				),
			enableSorting: false,
		},
		{
			accessorKey: "isActive",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.status")}
				/>
			),
			cell: ({ row }) => (
				<div className="flex items-center gap-2 text-nowrap">
					<Switch
						checked={!!row.original.isActive}
						onCheckedChange={() => onToggleActive(row.original)}
						aria-label={t("button.toggle_status", {
							name: row.original.serviceName,
						})}
					/>
					<Badge
						variant="outline"
						className={cn(
							"border rounded-full text-xs font-semibold uppercase",
							row.original.isActive
								? "bg-emerald-50 text-emerald-700 border-emerald-200"
								: "bg-rose-50 text-rose-700 border-rose-200",
						)}
					>
						{row.original.isActive ? t("status.ACTIVE") : t("status.INACTIVE")}
					</Badge>
				</div>
			),
			enableSorting: false,
		},
		{
			accessorKey: "maxRequestsPerMinute",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.max_requests")}
				/>
			),
			meta: {
				headerClassName: "hidden md:table-cell",
				cellClassName: "hidden md:table-cell text-nowrap",
			},
			cell: ({ row }) =>
				row.original.maxRequestsPerMinute == null
					? t("table.not_available")
					: t("table.requests_per_minute", {
							count: row.original.maxRequestsPerMinute,
						}),
			enableSorting: false,
		},
		{
			accessorKey: "lastUsedAt",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title={t("table.header.last_used_at")}
				/>
			),
			meta: {
				headerClassName: "hidden md:table-cell",
				cellClassName: "hidden md:table-cell text-nowrap",
			},
			cell: ({ row }) =>
				row.original.lastUsedAt ? (
					format(new Date(row.original.lastUsedAt), "PPp", {
						locale,
						in: tzDate,
					})
				) : (
					<span className="text-muted-foreground italic">
						{t("table.never_used")}
					</span>
				),
			enableSorting: false,
		},
		{
			id: "actions",
			header: () => (
				<span className="sr-only">{t("table.header.actions")}</span>
			),
			cell: ({ row }) => (
				<div className="flex justify-end">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="border size-8 shadow-none"
								aria-label={t("button.open_actions")}
							>
								<MoreHorizontal className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel>{t("table.header.actions")}</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onEdit(row.original);
								}}
							>
								<PencilIcon className="size-4" />
								{t("button.edit")}
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onToggleActive(row.original);
								}}
							>
								<PowerIcon className="size-4" />
								{row.original.isActive
									? t("button.revoke")
									: t("button.activate")}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={(event) => {
									event.preventDefault();
									onDelete(row.original);
								}}
							>
								<Trash2Icon className="size-4" />
								{t("button.remove")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			),
			enableSorting: false,
		},
	];
}
